import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec, formatNumberMagnitude, makePlural } from '../helpers/stringFormat';
import { formatString, getMagnitude } from './valueFunctions';
import { addCurrencyFrequency } from '../helpers/currencyFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { createSliderMarks, getSliderInitialValue } from './formFunctions';
import { toolTipUserIcon } from './iconFunctions';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const frequencyTransfer = {
    daily: "day",
    monthly: "month",
    quarterly: "quarter",
    yearly: "year",
};


const CostValueDriverInvestmentForm = forwardRef(({ currency, outcome, outcomeList, ownProduct, costStructure, editInvestmentDriverId, editInvestmentDriver, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [selectedType, setSelectedType] = useState(costStructure.multiple_type[0])
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        usefulLife: 'Useful life refers to the estimated period during which an asset or product is expected to remain functional and provide economic value before it needs to be replaced or becomes obsolete.',
        shareOfCost: "Indicates the percentage of cost affected by the efficiency change (saving or cost increase). E.g., 100% means all of the cost are affected; 50% means half are.",
        saving: `${positiveDriver ? 'Savings' : 'Extra costs'} relative to your competitive alternative (the one stated when you created your Product).`,
        projectShortening: `Project ${positiveDriver ? 'shortening' : 'lengthening'} (in days) measures the change in project duration achieved through through using your product compared to the competitive alternative. It is used in calculating change in refinancing costs.`,
        interestRateSpread: "Interest rate spread in refinancing is the difference between the existing loan’s interest rate and the new refinanced rate. A favorable spread occurs when the new rate is lower, leading to cost savings over time, whereas a negative spread indicates a higher cost.",
        need: "Quantify the need for your project in completing the project."
    }

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
        formik.setFieldValue('multiple_type', event.target.value)
        formik.setFieldValue('cost_per_multiple', getSliderInitialValue(costStructure, event.target.value))
    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        setSelectedType(costStructure.multiple_type[0])
        formik.setFieldValue("multiple_type", costStructure.multiple_type[0])
        formik.setFieldValue("cost_per_multiple", getSliderInitialValue(costStructure, costStructure.multiple_type[0]))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costStructure]);

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editInvestmentDriverId) {
            setPositiveDriver(editInvestmentDriver["positive_driver"])

            formik.setFieldValue("multiple_type", editInvestmentDriver["multiple_type"])
            formik.setFieldValue("cost_per_multiple", editInvestmentDriver["cost_per_multiple"])
            formik.setFieldValue("efficiency_gain", editInvestmentDriver["efficiency_gain"])
            formik.setFieldValue("share_of_cost", editInvestmentDriver["share_of_cost"])
            formik.setFieldValue("multiple_value", editInvestmentDriver["multiple_value"])
            formik.setFieldValue("useful_life", editInvestmentDriver["useful_life"])
            formik.setFieldValue("interest_rate_spread", editInvestmentDriver["interest_rate_spread"])
            formik.setFieldValue("project_length", editInvestmentDriver["project_length"])
            formik.setFieldValue("project_shortening", editInvestmentDriver["project_shortening"])
            formik.setFieldValue("units_needed", formatNumberThsndAndDec(editInvestmentDriver["units_needed"]))
            formik.setFieldValue("positive_driver", editInvestmentDriver["positive_driver"])
            formik.setFieldValue("outcome", editInvestmentDriver["outcome"])
            formik.setFieldValue("driver_name", editInvestmentDriver["driver_name"])
            formik.setFieldValue("comment", editInvestmentDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editInvestmentDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `${formatString(selectedType)} Project ${positiveDriver ? 'Savings' : 'Extra'}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver, selectedType])

    const initialValues = {
        id: null,
        cost_type: 'investment',
        outcome: '',
        driver_name: `${formatString(selectedType)} Project ${positiveDriver ? 'Savings' : 'Extra'}`,
        comment: '',
        positive_driver: true,
        type: costStructure.type,
        currency: currency,
        multiple_type: selectedType,
        multiple: costStructure.multiple,
        multiple_value: 0,
        cost_per_multiple: getSliderInitialValue(costStructure, selectedType),
        efficiency_gain: 0,
        share_of_cost: 100,
        useful_life: 15,
        interest_rate_spread: 0,
        project_shortening: 0,
        units_needed: 0,
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};
        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        if (!isNumeric(values.useful_life)) {
            errors.useful_life = 'Must be numeric value';
        } else if (!isPositive(values.useful_life)) {
            errors.useful_life = 'Must be positive or zero';
        }

        if (!isNumeric(values.efficiency_gain)) {
            errors.efficiency_gain = 'Must be numeric value';
        } else if (!isPositive(values.efficiency_gain)) {
            errors.efficiency_gain = 'Must be positive or zero';
        } else if (values.efficiency_gain > 100) {
            errors.efficiency_gain = 'Not more than 100';
        }

        if (!isNumeric(values.share_of_cost)) {
            errors.share_of_cost = 'Must be numeric value';
        } else if (!isPositive(values.share_of_cost)) {
            errors.share_of_cost = 'Must be positive or zero';
        } else if (values.share_of_cost > 100) {
            errors.share_of_cost = 'Not more than 100';
        }

        if (!isNumeric(values.project_shortening)) {
            errors.project_shortening = 'Must be numeric value';
        } else if (!isPositive(values.project_shortening)) {
            errors.project_shortening = 'Must be positive or zero';
        }

        if (!isNumeric(values.interest_rate_spread)) {
            errors.interest_rate_spread = 'Must be numeric value';
        } else if (!isPositive(values.interest_rate_spread)) {
            errors.interest_rate_spread = 'Must be positive or zero';
        } else if (values.interest_rate_spread > 30) {
            errors.interest_rate_spread = 'That is a high number';
        }

        if (!isNumeric(values.units_needed)) {
            errors.units_needed = 'Must be numeric value';
        } else if (!isPositive(values.units_needed)) {
            errors.units_needed = 'Must be positive or zero';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setSelectedType(costStructure.multiple_type[0])
            setPositiveDriver(true)
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            formik.setFieldValue("multiple_type", costStructure.multiple_type[0])
            formik.setFieldValue("cost_per_multiple", getSliderInitialValue(costStructure, costStructure.multiple_type[0]))
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1200 }}>
            <Grid container columnSpacing={2} rowSpacing={2} sx={{ mt: 2 }}>
                {costStructure.multiple_type.map(type => {
                    if (type === selectedType) {
                        return (
                            <React.Fragment key={type}>
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            2. Project costs
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            Project type
                                        </Typography>
                                        <Box>
                                            <Select
                                                open={open}
                                                onClose={handleClose}
                                                onOpen={handleOpen}
                                                value={formik.values.multiple_type}
                                                onChange={handleTypeChange}
                                                size='small'
                                            >
                                                {costStructure.multiple_type.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {/* {firstCharToUpper(type)} */}
                                                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                            {firstCharToUpper(type)}
                                                            {costStructure.cost_per_multiple[type]?.user_created && toolTipUserIcon()}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={3}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            {`Cost per ${costStructure.multiple}`}
                                        </Typography>
                                        < Slider
                                            name="cost_per_multiple"
                                            value={formik.values.cost_per_multiple}
                                            onChange={formik.handleChange}
                                            valueLabelDisplay="auto"
                                            marks={createSliderMarks(costStructure.cost_per_multiple[type])}
                                            min={costStructure.cost_per_multiple[type].lower * 0}
                                            max={costStructure.cost_per_multiple[type].higher * 1.2}
                                            step={getMagnitude(costStructure.cost_per_multiple[type].lower) / 10}
                                            valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), costStructure.cost_per_multiple[type].currency, null)}
                                            sx={{ maxWidth: 300 }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Useful life{tooltip(tooltipText.usefulLife)}
                                        </Typography>
                                        <TextFieldCustom
                                            name="useful_life"
                                            endAdornment={
                                                <Typography variant="caption">
                                                    years
                                                </Typography>
                                            }
                                            value={formik.values.useful_life}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("useful_life", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "useful_life") && Boolean(getIn(formik.errors, "useful_life"))}
                                            helperText={getIn(formik.touched, "useful_life") && getIn(formik.errors, "useful_life")}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={7} />
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                % of cost{tooltip(tooltipText.shareOfCost)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                {positiveDriver ? 'Savings' : 'Cost increase'}{tooltip(tooltipText.saving)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8} />
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="share_of_cost"
                                                endAdornment="%"
                                                value={formik.values.share_of_cost}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("share_of_cost", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "share_of_cost") && Boolean(getIn(formik.errors, "share_of_cost"))}
                                                helperText={getIn(formik.touched, "share_of_cost") && getIn(formik.errors, "share_of_cost")}
                                            />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="efficiency_gain"
                                                endAdornment="%"
                                                value={formik.values.efficiency_gain}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("efficiency_gain", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "efficiency_gain") && Boolean(getIn(formik.errors, "efficiency_gain"))}
                                                helperText={getIn(formik.touched, "efficiency_gain") && getIn(formik.errors, "efficiency_gain")}
                                                greenBackground={positiveDriver}
                                                redBackground={!positiveDriver}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={8} />
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            3. Refinancing
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                {`Project ${positiveDriver ? 'shortening' : 'lengthening'}`}{tooltip(tooltipText.projectShortening)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Interest rate spread{tooltip(tooltipText.interestRateSpread)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8} />
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="project_shortening"
                                                endAdornment={
                                                    <Typography variant="caption">
                                                        days
                                                    </Typography>
                                                }
                                                value={formik.values.project_shortening}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("project_shortening", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "project_shortening") && Boolean(getIn(formik.errors, "project_shortening"))}
                                                helperText={getIn(formik.touched, "project_shortening") && getIn(formik.errors, "project_shortening")}
                                                greenBackground={positiveDriver}
                                                redBackground={!positiveDriver}
                                            />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="interest_rate_spread"
                                                endAdornment="%"
                                                value={formik.values.interest_rate_spread}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("interest_rate_spread", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "interest_rate_spread") && Boolean(getIn(formik.errors, "interest_rate_spread"))}
                                                helperText={getIn(formik.touched, "interest_rate_spread") && getIn(formik.errors, "interest_rate_spread")}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={8} />
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            4. Need for {ownProduct.name}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container columnGap={2}>
                                        {ownProduct.revenue_model === 'transactional' ?
                                            <Grid item xs={2}>
                                                <Typography variant="caption">
                                                    {ownProduct.name} needed for project{tooltip(tooltipText.need)}
                                                </Typography>
                                            </Grid>
                                            :
                                            <Grid item xs={2}>
                                                <Typography variant="caption">
                                                    {firstCharToUpper(makePlural(frequencyTransfer[ownProduct.frequency]))} subscription of {ownProduct.name} needed for project{tooltip(tooltipText.need)}
                                                </Typography>
                                            </Grid>
                                        }
                                        <Grid item xs={10} />
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="units_needed"
                                                endAdornment={
                                                    <Typography variant="caption">
                                                        {ownProduct.revenue_model === 'transactional' ? makePlural(ownProduct.unit) : makePlural(frequencyTransfer[ownProduct.frequency])}
                                                    </Typography>
                                                }
                                                value={formik.values.units_needed}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("units_needed", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "units_needed") && Boolean(getIn(formik.errors, "units_needed"))}
                                                helperText={getIn(formik.touched, "units_needed") && getIn(formik.errors, "units_needed")}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            5. Driver details
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                                        </Typography>
                                        <GreenRedCompactSwitch
                                            name="positive_driver"
                                            leftLabel="Negative"
                                            rightLabel="Positive"
                                            value={formik.values.positive_driver}
                                            onChange={(event) => {
                                                formik.handleChange(event)
                                                setPositiveDriver(!positiveDriver)
                                            }}
                                        />

                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                {!outcome &&
                                    (<React.Fragment>
                                        <Grid item xs={2}>
                                            <Stack direction="column" gap={1}>
                                                <Typography variant="caption">
                                                    Outcome
                                                </Typography>
                                                <Box>
                                                    <Select
                                                        open={openOutcome}
                                                        onClose={handleOutcomeClose}
                                                        onOpen={handleOutcomeOpen}
                                                        value={formik.values.outcome}
                                                        onChange={handleOutcomeChange}
                                                        size='small'
                                                    >
                                                        {outcomeList.map((outcome) => (
                                                            <MenuItem key={outcome} value={outcome}>
                                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                    {outcome}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={10} />
                                    </React.Fragment>)
                                }
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Name of driver
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Comment
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3} />
                                        <Grid item xs={3}>
                                            <TextFieldCustom
                                                name="driver_name"
                                                value={formik.values.driver_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextFieldCustom
                                                name="comment"
                                                value={formik.values.comment}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        )
                    }
                    else {
                        return null;
                    }
                })
                }
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default CostValueDriverInvestmentForm                